import React from 'react';
import { IonButton, IonPopover } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { SortIcon, CheckIcon } from '../../theme/Icons';
import { MenuItem } from '../../components/MenuItem';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

const useStyles = createUseStyles({
    sortButton: {
        '--padding-start': '8px',
        '--padding-end': '8px',
        fontSize: '14px !important',
        fontWeight: '500 !important',
        height: 'auto',
        minHeight: '36px',
    },
    sortPopover: {
        '&::part(content)': {
            minWidth: 160
        },
        '& ion-label': {
            fontSize: '14px !important'
        }
    }
});

export type SortField = 'updatedAt' | 'createdAt';
export type SortOrder = 'newest' | 'oldest';

interface INotesSortControlProps {
    sortField: SortField;
    sortOrder: SortOrder;
    onSortFieldChange: (field: SortField) => void;
    onSortOrderChange: (order: SortOrder) => void;
    triggerId?: string;
    isMobile?: boolean;
    iconOnly?: boolean;
}

export const NotesSortControl: React.FC<INotesSortControlProps> = ({
    sortField,
    sortOrder,
    onSortFieldChange,
    onSortOrderChange,
    triggerId = 'sort-trigger',
    isMobile = false,
    iconOnly = false
}) => {
    const classes = useStyles();
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const handleSortFieldChange = (newField: SortField) => {
        // Close popover and update state immediately for responsive UI
        setPopoverOpen(false);
        onSortFieldChange(newField);
        // Track asynchronously to avoid blocking UI
        if (newField !== sortField) {
            try {
                mixpanel.track('Notes Sort Field Changed', () => ({
                    'Sort Field': newField,
                    'Previous Field': sortField
                }));
            } catch (error) {
                console.error('Failed to track Notes Sort Field Changed:', error);
            }
        }
    };

    const handleSortOrderChange = (newOrder: SortOrder) => {
        // Close popover and update state immediately for responsive UI
        setPopoverOpen(false);
        onSortOrderChange(newOrder);
        // Track asynchronously to avoid blocking UI
        if (newOrder !== sortOrder) {
            try {
                mixpanel.track('Notes Sort Order Changed', () => ({
                    'Sort Order': newOrder,
                    'Previous Order': sortOrder
                }));
            } catch (error) {
                console.error('Failed to track Notes Sort Order Changed:', error);
            }
        }
    };

    return (
        <>
            <IonButton
                fill="clear"
                color="medium"
                id={triggerId}
                onClick={() => setPopoverOpen(true)}
                className={classes.sortButton}
                data-id="sort-button"
            >
                <SortIcon size="medium" color="medium" />
                {!iconOnly && <span style={{marginLeft: '4px'}}>Sort By</span>}
            </IonButton>

            <IonPopover
                isOpen={popoverOpen}
                trigger={triggerId}
                alignment="end"
                onDidDismiss={() => setPopoverOpen(false)}
                className={classes.sortPopover}
            >
                <MenuItem
                    onClick={() => handleSortFieldChange('updatedAt')}
                    label="Date Modified (Default)"
                    Icon={sortField === 'updatedAt' ? CheckIcon : undefined}
                    iconColor="primary"
                    leaveSpaceForIcon
                    data-id="sort-field-updatedAt"
                />
                <MenuItem
                    onClick={() => handleSortFieldChange('createdAt')}
                    label="Date Created"
                    Icon={sortField === 'createdAt' ? CheckIcon : undefined}
                    iconColor="primary"
                    leaveSpaceForIcon
                    data-id="sort-field-createdAt"
                />

                <div style={{height: '1px', backgroundColor: 'var(--ion-color-step-150, #e0e0e0)', margin: '8px 0'}} />

                <MenuItem
                    onClick={() => handleSortOrderChange('newest')}
                    label="Newest First (Default)"
                    Icon={sortOrder === 'newest' ? CheckIcon : undefined}
                    iconColor="primary"
                    leaveSpaceForIcon
                    data-id="sort-order-newest"
                />
                <MenuItem
                    onClick={() => handleSortOrderChange('oldest')}
                    label="Oldest First"
                    Icon={sortOrder === 'oldest' ? CheckIcon : undefined}
                    iconColor="primary"
                    leaveSpaceForIcon
                    data-id="sort-order-oldest"
                />
            </IonPopover>
        </>
    );
};
